/* eslint-disable @typescript-eslint/no-explicit-any */
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { isNotNil, omit, path, prop, uniqBy } from 'ramda'
import { clone } from 'widget-components/utilities/clone'
import type {
    ActionButton,
    ApiCustomPageComponents,
    BaseBlock,
    ComponentOptions,
    ComponentsWithOptions,
    CustomPageComponents,
    DropdownItem,
    ExtendedBlock,
    InputDate,
    InputDateTime,
    InputDropdown,
    InputRadio,
    ListView,
    ListViewApi,
    InputCheckbox,
    InputConsentList,
    CPInputConfiguration,
    InputSlider,
    ContainerApi,
    Container,
    InputCountry,
    InputPhoneNumber,
    InputAddress,
    EmptySpace,
    RepeatableContainerApi,
    RepeatableContainer,
    RepeatableContainerIterationAddedEvent,
    TabbedContainerApi,
    TabbedContainer,
    GraphApi,
    Graph,
    GraphNodeApi,
    GraphNode,
    GraphEdgeApi,
    GraphEdge,
    InputDocument,
    InputFile,
    Resource,
    BlockPath,
} from './types'
import dayjs from 'dayjs'
import components from 'widget-components/components'
import { type ComponentInstance, nextTick } from 'vue'
import { emitBlockUpdate } from './utilities/emitBlockUpdate'
import equals from 'fast-deep-equal/es6'
import { useEventBus } from '@vueuse/core'
import { BLOCK_TYPES } from './consts'

type ListenerEmit = (event: string, ...args: any[]) => void

dayjs.extend(customParseFormat)

type NestedComponent = { id?: string, currentValue?: unknown, children?: NestedComponent[] }

// what the user typed into the edit dialog but has not submitted, keyed by component id: a mutation
// answers with the graph as the *server* has it, which knows nothing about those values
const typedGraphValues = (blocks: NestedComponent[], values = new Map<string, unknown>()) => {
    blocks.forEach(block => {
        if (block.id && block.currentValue !== undefined) {
            values.set(block.id, block.currentValue)
        }

        typedGraphValues(block.children || [], values)
    })

    return values
}

const carryTypedValues = <T extends NestedComponent>(blocks: T[], values: Map<string, unknown>): T[] =>
    blocks.map(block => ({
        ...block,
        ...(block.id && values.has(block.id) ? { currentValue: values.get(block.id) } : {}),
        ...(block.children ? { children: carryTypedValues(block.children, values) } : {}),
    }))

// `getDisplayValue` below reads a value the way its own component would; see `getBlockDisplayValue`
const isValueRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const scalarText = (value: unknown): string | undefined => {
    if (typeof value === 'string') return value.trim() || undefined
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined
    if (typeof value === 'boolean') return String(value)

    return undefined
}

const entriesText = (value: unknown, textOf: (item: Record<string, unknown>) => unknown) =>
    (Array.isArray(value) ? value : [value])
        .filter(isValueRecord)
        .map(item => scalarText(textOf(item)))
        .filter(Boolean)
        .join(', ') || undefined

const optionText = (value: unknown) => entriesText(value, item => item.label ?? item.value)

const fileNamesText = (value: unknown) => {
    const document = isValueRecord(value) ? value.document : undefined
    const files = isValueRecord(document) ? document.files : undefined

    return isValueRecord(files) ? entriesText(Object.values(files), file => file.name) : undefined
}

const addressText = (value: unknown) =>
    isValueRecord(value)
        ? [value.addressLine1, value.addressLine2, value.postalCode, value.city, value.region, value.countryCode]
            .map(scalarText).filter(Boolean).join(', ') || undefined
        : undefined

export const BLOCK_MAP: Record<string, BaseBlock> = {
    TEXT_TITLE: {
        name: 'TextTitle',
        type: BLOCK_TYPES.TEXT,
        component: () => components.TextTitle,
    },
    TEXT_PLAIN: {
        name: 'TextPlain',
        type: BLOCK_TYPES.TEXT,
        component: () => components.TextPlain,
    },
    COUNTRY: {
        name: 'Country',
        type: BLOCK_TYPES.TEXT,
        component: () => components.Country,
    },
    TEXT_COPIABLE: {
        name: 'TextCopiable',
        type: BLOCK_TYPES.TEXT,
        component: () => components.TextCopiable,
    },
    GRAPHIC_IMAGE: {
        name: 'GraphicImage',
        type: BLOCK_TYPES.GRAPHIC,
        component: () => components.GraphicImage,
    },
    EMPTY_SPACE: {
        name: 'EmptySpace',
        type: BLOCK_TYPES.GRAPHIC,
        transformBindings: (block: ExtendedBlock<EmptySpace>) => ({
            ...block,
            hasCustomWidth: !!path(['style', '--tl-empty-space-width'], block),
        }),
        component: () => components.EmptySpace,
    },
    DIVIDER: {
        name: 'Divider',
        type: BLOCK_TYPES.GRAPHIC,
        component: () => components.Divider,
    },
    INPUT_RADIO: {
        name: 'InputRadio',
        type: BLOCK_TYPES.FORM,
        bindings: {
            itemLabel: (i?: DropdownItem) => i?.label,
            itemValue: (i?: DropdownItem) => i?.value,
        },
        getInitialValue(block: InputRadio) {
            if (!block.selectedItemId) {
                return ''
            }

            return block.items.find(b => b.itemId === block.selectedItemId)?.value || ''
        },
        component: () => components.InputRadio,
    },
    INPUT_CHECKBOX: {
        name: 'InputCheckbox',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputCheckbox,
        getInitialValue(block: InputCheckbox) {
            return block.selected || false
        },
    },
    TOGGLE: {
        name: 'Toggle',
        type: BLOCK_TYPES.FORM,
        component: () => components.Toggle,
        getInitialValue(block: InputCheckbox) {
            return block.selected || false
        },
    },
    INPUT_DIGIT: {
        name: 'InputDigit',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputDigit,
    },
    INPUT_DATE: {
        name: 'InputDate',
        type: BLOCK_TYPES.FORM,
        getInitialValue: (block: InputDate) => {
            return transformDateBlockBindings(block.format, block.type, block.defaultValue)
        },
        component: () => components.InputDate,
    },
    INPUT_DATE_TIME: {
        name: 'InputDateTime',
        type: BLOCK_TYPES.FORM,
        getInitialValue: (block: InputDate) => {
            return transformDateBlockBindings(block.format, block.type, block.defaultValue)
        },
        component: () => components.InputDateTime,
    },
    INPUT_SLIDER: {
        name: 'InputSlider',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputSlider,
        getInitialValue: (block: InputSlider) => {
            return block.defaultValue ?? block.min
        },
    },
    INPUT_CONSENT_LIST: {
        name: 'InputConsentList',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputConsentList,
        getInitialValue: (block: InputConsentList) => {
            return block.items
        },
        // an unselected consent is not part of the answer
        getDisplayValue: (value: unknown) =>
            entriesText(value, item => (item.selected ? item.shortDesc : undefined)),
    },
    INPUT_TEXT: {
        name: 'InputText',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputText,
    },
    INPUT_NUMBER: {
        name: 'InputNumber',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputNumber,
    },
    INPUT_EMAIL: {
        name: 'InputEmail',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputEmail,
    },
    INPUT_ADDRESS: {
        name: 'InputAddress',
        type: BLOCK_TYPES.FORM,
        getInitialValue: (block: InputAddress) => {
            if (!block.defaultCountry && !block.defaultValue) {
                return null
            }
            return {
                ...block.defaultValue,
                countryCode: block.defaultValue?.countryCode || block.defaultCountry?.countryCode || null,
            }
        },
        transformBindings: (block: ExtendedBlock<InputAddress>) => ({
            countries: block.items,
            autocompleteEnabled: block.autocompleteEnabled,
            defaultCountry: block.defaultCountry,
        }),
        getDisplayValue: addressText,
        component: () => components.InputAddress,
    },
    INPUT_TEXT_AREA: {
        name: 'InputTextArea',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputTextArea,
    },
    INPUT_DROPDOWN: {
        name: 'InputDropdown',
        type: BLOCK_TYPES.FORM,
        bindings: {
            itemLabel: (i?: DropdownItem) => i?.label,
            itemValue: (i?: DropdownItem) => i?.value,
        },
        transformBindings: (block: ExtendedBlock<InputDropdown>) => {
            return {
                ...block.requiredCondition || block.required
                    ? { required: block.requiredCondition?.type === 'ALWAYS' || block.required }
                    : {},
            }
        },
        getInitialValue: (block: InputDropdown) => {
            const { multiple, items } = block

            if (multiple) {
                return items?.filter(i => i.selected)
            }

            return items?.find(b => b.selected)
        },
        getDisplayValue: optionText,
        component: () => components.InputDropdown,
    },
    INPUT_COUNTRY: {
        name: 'InputCountry',
        type: BLOCK_TYPES.FORM,
        getInitialValue: (block: InputCountry) => {
            const { multiple, items: rawItems, selectedItems } = block

            const items = uniqBy(prop<string>('itemId'), [
                ...(Array.isArray(rawItems) ? rawItems : []),
                ...(Array.isArray(selectedItems) ? selectedItems.map(i => ({ ...i, selected: true })) : []),
            ])

            if (multiple) {
                return items?.filter(i => i.selected)
            }

            return items?.find(b => b.selected)
        },
        getDisplayValue: optionText,
        component: () => components.InputCountry,
    },
    INPUT_PHONE_NUMBER: {
        name: 'InputPhoneNumber',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputPhoneNumber,
        bindings: {
            modelValueFormat: 'object',
        },
        getDisplayValue: (value: unknown) =>
            (isValueRecord(value) ? scalarText(value.fullNumber) : undefined),
        getInitialValue: (block: InputPhoneNumber) => {
            return {
                countryCode: block.defaultValue?.countryCode ? `+${block.defaultValue?.countryCode}` : '',
                number: block.defaultValue?.number || '',
                fullNumber: block.defaultValue?.countryCode && block.defaultValue?.number ? `+${block.defaultValue?.countryCode}${block.defaultValue?.number}` : '',
            }
        },
    },
    INPUT_DOCUMENT: {
        name: 'InputDocument',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputDocument,
        getDisplayValue: fileNamesText,
        getInitialValue: (block: InputDocument) => {
            if (block.defaultResources?.length) {
                return ({
                    document: {
                        files: (block.defaultResources).reduce((acc, resource) => {
                            acc[`${resource.resourceId}`] = {
                                name: resource.resourceName,
                                size: '1',
                                data: 'data',
                                resource,
                            }
                            return acc
                        }, {} as Record<string, any>),
                    },
                })
            }

            return {
                document: { files: {} },
            }
        },
    },
    INPUT_FILE: {
        name: 'InputFile',
        type: BLOCK_TYPES.FORM,
        component: () => components.InputFile,
        getDisplayValue: fileNamesText,
        getInitialValue: (block: InputFile) => {
            if (block.defaultResources?.length) {
                return ({
                    document: {
                        files: (block.defaultResources).reduce((acc: Record<string, any>, resource: Resource) => {
                            acc[`${resource.resourceId}`] = {
                                name: resource.resourceName,
                                size: '1',
                                data: 'data',
                                resource,
                            }
                            return acc
                        }, {} as Record<string, any>),
                    },
                })
            }

            return {
                document: { files: {} },
            }
        },
    },
    INPUT_CHECKBOX_LIST: {
        name: 'InputCheckboxList',
        type: BLOCK_TYPES.FORM,
        // the value is already the chosen subset
        getDisplayValue: (value: unknown) => entriesText(value, item => item.label),
        component: () => components.InputCheckboxList,
    },
    SELECTION_LIST: {
        name: 'SelectionList',
        type: BLOCK_TYPES.ACTION,
        component: () => components.SelectionList,
    },
    NAVIGATION: {
        name: 'Navigation',
        type: BLOCK_TYPES.ACTION,
        component: () => components.Navigation,
    },
    LANGUAGE_SELECTOR: {
        name: 'LanguageSelector',
        type: BLOCK_TYPES.OTHER,
        component: () => components.LanguageSelector,
        listeners: {
            changeLang: (_, __, langCode: string) => {
                useEventBus<string>('custom-page-change-lang').emit(langCode)
            },
        },
    },
    ACTION_BUTTON: {
        name: 'ActionButton',
        type: BLOCK_TYPES.ACTION,
        transformBindings(block: ActionButton) {
            return {
                secondary: (block).variant === 'OUTLINE',
            }
        },
        component: () => components.ActionButton,
    },
    DISPLAY_DOCUMENT: {
        name: 'DisplayDocument',
        type: BLOCK_TYPES.OTHER,
        component: () => components.DisplayDocument,
    },
    PROGRESS_BAR: {
        name: 'ProgressBar',
        type: BLOCK_TYPES.OTHER,
        component: () => components.ProgressBar,
    },
    STEPPER: {
        name: 'Stepper',
        type: BLOCK_TYPES.OTHER,
        component: () => components.Stepper,
    },
    LIST_VIEW: {
        name: 'ListView',
        type: BLOCK_TYPES.FORM,
        transformBindings(block: ExtendedBlock<ListViewApi>, indexPath: number[]) {
            const items = (block.items || [])
                .reduce((acc, _item) => {
                    const item = clone(_item) as unknown as ListView['items'][number]

                    item.components = (_item.components || [])
                        .map((component, index) =>
                            transformCustomPageBlock(component, [...indexPath, index], undefined, true) as ExtendedBlock<CustomPageComponents>)

                    acc.push(item as ListView['items'][number])

                    return acc
                }, [] as ListView['items'])

            return {
                items,
            }
        },
        listeners: {
            'items:add': (emit: ListenerEmit, _block: ExtendedBlock<ListView>) => {
                const block = clone(_block)
                const newItem = {
                    index: Math.max(...block.items.map(i => i.index)) + 1,
                    components: block.items[0].components.map(c => ({
                        ...clone(c),
                        initialValue: undefined,
                        currentValue: undefined,
                    })),
                }
                block.items = [...block.items, newItem]
                block.currentValue = [...block.currentValue, new Array(newItem.components.length)]
                block.initialValue = [...block.initialValue, new Array(newItem.components.length)]
                emit('update:block', block)
            },
            'items:remove': (emit: ListenerEmit, _block: ExtendedBlock<ListView>, idx: number) => {
                const block = clone(_block)
                if (block.items.length > 1) {
                    block.items.splice(idx, 1)
                    block.currentValue.splice(idx, 1)
                    block.initialValue.splice(idx, 1)
                }
                emit('update:block', block)
            },
        },
        getInitialValue: (block: ListView) => {
            return block.items.map((b, i) => (b.components ?? []).map((_, j) => path(['items', i, 'components', j, 'currentValue'], block)))
        },
        component: () => components.ListView,
    },
    REPEATABLE_CONTAINER: {
        name: 'RepeatableContainer',
        type: BLOCK_TYPES.FORM,
        transformBindings(block: ExtendedBlock<RepeatableContainerApi>, indexPath: number[], blockPath?: (number|string)[]) {
            const iterations = (block.children || [])
                .map((iteration, iterationIndex) => {
                    const iterationComponents = clone(iteration) as unknown as RepeatableContainer['children'][number]
                    return (iterationComponents || []).map((c, i) => {
                        const transformedChild = transformCustomPageBlock(c, [...indexPath, i], [...(blockPath || []), 'children', iterationIndex, i])
                        return transformedChild as ExtendedBlock<CustomPageComponents>
                    })
                })
            return {
                children: iterations,
            }
        },
        listeners: {
            'items:add': (_, _block: ExtendedBlock<RepeatableContainer>, newItem: RepeatableContainerApi) => {
                useEventBus('block-component-change-start').emit()
                const block = clone(_block)
                const newChildren = newItem.children.pop()
                const newIterationIndex = _block.children.length

                if (newChildren) {
                    block.children.push(newChildren as ExtendedBlock<CustomPageComponents>[])
                    emitBlockUpdate(block.blockPath, transformCustomPageBlock(block, block.indexPath, block.blockPath))

                    useEventBus<string>('block-component-changed').emit(block.id)

                    nextTick(() => {
                        useEventBus<RepeatableContainerIterationAddedEvent>('repeatable-container:iteration-added').emit({
                            block,
                            newIterationIndex,
                        })
                    })
                }

            },
            'items:remove': (_, _block: ExtendedBlock<RepeatableContainer>, idx: number) => {
                useEventBus('block-component-change-start').emit()
                const block = clone(_block)
                if (block.children.length > 0) {
                    block.children.splice(idx, 1)
                }

                emitBlockUpdate(block.blockPath, transformCustomPageBlock(block, block.indexPath, block.blockPath))
                useEventBus<string>('block-component-changed').emit(block.id)
            },
        },
        component: () => components.RepeatableContainer,
    },
    TABBED_CONTAINER: {
        name: 'TabbedContainer',
        type: BLOCK_TYPES.FORM,
        transformBindings(block: TabbedContainerApi, indexPath: number[], blockPath?: (number|string)[]) {
            const children = (block.children || [])
                .map((_child, index) => {
                    const child = clone(_child) as unknown as TabbedContainer['children'][number]
                    return transformCustomPageBlock(child, [...indexPath, index], [...(blockPath || []), 'children', index]) as ExtendedBlock<CustomPageComponents>
                })

            return {
                children,
                name: 'TabbedContainer',
            }
        },
        component: () => components.TabbedContainer,
    },
    CONTAINER: {
        name: 'Container',
        type: BLOCK_TYPES.FORM,
        transformBindings(block: ContainerApi, indexPath: number[], blockPath?: (number|string)[]) {
            const children = (block.children || [])
                .map((_child, index) => {
                    const child = clone(_child) as unknown as Container['children'][number]
                    return transformCustomPageBlock(child, [...indexPath, index], [...(blockPath || []), 'children', index]) as ExtendedBlock<CustomPageComponents>
                })

            return {
                children,
                name: 'Container' + (block.label ? ' - ' + block.label : ''),
            }
        },
        component: () => components.Container,
    },
    // A graph holds no value of its own, so BLOCK_TYPES.OTHER and no getInitialValue. The inputs
    // inside its drawn nodes are ordinary form blocks, mutated through the graph endpoints rather
    // than by the user, hence the listener.
    GRAPH: {
        name: 'Graph',
        type: BLOCK_TYPES.OTHER,
        transformBindings(block: GraphApi, indexPath: number[], blockPath?: (number|string)[]) {
            // `nodes` are the drawn nodes at runtime; the builder feeds the same block its authored
            // kinds instead, node kinds then edge kinds, which is the order it addresses them by
            const drawn = [...block.nodes ?? [], ...block.edges ?? []]

            const children = drawn.map((_child, index) => {
                const child = clone(_child) as unknown as Graph['children'][number]
                return transformCustomPageBlock(child, [...indexPath, index], [...(blockPath || []), 'children', index]) as ExtendedBlock<CustomPageComponents>
            })

            return {
                children,
                name: 'Graph' + (block.fieldName ? ' - ' + block.fieldName : ''),
            }
        },
        listeners: {
            'graph:update': (_, _block: ExtendedBlock<Graph>, graph: GraphApi) => {
                useEventBus('block-component-change-start').emit()
                const block = clone(_block) as ExtendedBlock<Graph> & Pick<GraphApi, 'nodes'>

                const typed = typedGraphValues(_block.children as NestedComponent[])

                block.nodes = carryTypedValues((graph.nodes ?? []) as NestedComponent[], typed) as CustomPageComponents[]
                block.nodeKinds = graph.nodeKinds
                block.edgeKinds = graph.edgeKinds

                emitBlockUpdate(block.blockPath, transformCustomPageBlock(block, block.indexPath, block.blockPath))
                useEventBus<string>('block-component-changed').emit(block.id)
            },
        },
        component: () => components.Graph,
    },
    GRAPH_NODE: {
        name: 'GraphNode',
        type: BLOCK_TYPES.OTHER,
        transformBindings(block: GraphNodeApi, indexPath: number[], blockPath?: (number|string)[]) {
            const children = (block.children || [])
                .map((_child, index) => {
                    const child = clone(_child) as unknown as GraphNode['children'][number]
                    return transformCustomPageBlock(child, [...indexPath, index], [...(blockPath || []), 'children', index]) as ExtendedBlock<CustomPageComponents>
                })

            return {
                children,
                name: 'GraphNode' + (block.key ? ' - ' + block.key : ''),
            }
        },
        component: () => components.Container,
    },
    GRAPH_EDGE: {
        name: 'GraphEdge',
        type: BLOCK_TYPES.OTHER,
        transformBindings(block: GraphEdgeApi, indexPath: number[], blockPath?: (number|string)[]) {
            const children = (block.children || [])
                .map((_child, index) => {
                    const child = clone(_child) as unknown as GraphEdge['children'][number]
                    return transformCustomPageBlock(child, [...indexPath, index], [...(blockPath || []), 'children', index]) as ExtendedBlock<CustomPageComponents>
                })

            const relation = `${block.from || '?'} → ${block.to || '?'}`

            return {
                children,
                name: `GraphEdge - ${relation}`,
            }
        },
        component: () => components.Container,
    },
}

const BOOLEAN_VALUE_COMPONENTS = [BLOCK_MAP.INPUT_CHECKBOX.name, BLOCK_MAP.TOGGLE.name]
const NUMBER_VALUE_COMPONENTS = [BLOCK_MAP.INPUT_SLIDER.name, BLOCK_MAP.INPUT_NUMBER.name]
const ARRAY_VALUE_COMPONENTS = [BLOCK_MAP.INPUT_CONSENT_LIST.name, BLOCK_MAP.LIST_VIEW.name]
const OBJECT_VALUE_COMPONENTS = [BLOCK_MAP.INPUT_DOCUMENT.name, BLOCK_MAP.INPUT_ADDRESS.name]

const getComponentInitialValue = (name: string) => {
    if (BOOLEAN_VALUE_COMPONENTS.includes(name)) {
        return false
    }

    if (NUMBER_VALUE_COMPONENTS.includes(name)) {
        return null
    }

    if (ARRAY_VALUE_COMPONENTS.includes(name)) {
        return []
    }

    if (OBJECT_VALUE_COMPONENTS.includes(name)) {
        return {}
    }

    return ''
}

/** A stored value as one line of text; `undefined` when it has no text form. */
export const getBlockDisplayValue = (type: string | undefined, value: unknown): string | undefined => {
    const template = BLOCK_MAP[type as keyof typeof BLOCK_MAP] as BaseBlock | undefined

    return template?.getDisplayValue?.(value) ?? scalarText(value)
}

const getBlockOptions = (configuration?: ComponentOptions) => {
    if (!configuration) {
        return null
    }

    return Object.fromEntries(Object.entries(configuration).map(([key, value]) => [key, typeof value === 'string' ? value.toLowerCase() : value]))
}

const getCurrentValue = (block: ApiCustomPageComponents, translatedBlock: BaseBlock) => {
    if (isNotNil((block as ExtendedBlock).currentValue)) {
        return (block as ExtendedBlock).currentValue
    }
    if (translatedBlock.getInitialValue) {
        return translatedBlock.getInitialValue(block)
    }
    return (block as CPInputConfiguration).defaultValue ?? getComponentInitialValue(translatedBlock.name)
}

const getBlockValue = (block: ApiCustomPageComponents, translatedBlock: BaseBlock) => {
    const currentValue = getCurrentValue(block, translatedBlock)
    return {
        currentValue,
        initialValue: clone(currentValue),
    }
}

const blocksCache = new Map<string, ExtendedBlock<any>>()

/**
 * Exported for hosts that render blocks which are not part of the page tree — the graph's edit
 * dialog authors an instance from clones of a kind's field templates. Such a caller must pass a
 * reminted `block.id` and `skipCache: true`: the id is what `blocksCache` and `findBlockPathById`
 * key on, so reusing the source id would let the clone resolve back to the real block.
 */
export const transformCustomPageBlock = <T = ApiCustomPageComponents>(block: ApiCustomPageComponents, indexPath: number[], blockPath?: BlockPath, skipCache?: boolean): ExtendedBlock<T> => {
    const blockTemplate = BLOCK_MAP[block.type as keyof typeof BLOCK_MAP]

    // eslint-disable-next-line no-autofix/@typescript-eslint/no-unnecessary-condition
    if (!blockTemplate) {
        throw new Error('Undefined component - specify details in BLOCK_MAP array:' + JSON.stringify(block))
    }

    const transformedBindings = (blockTemplate.transformBindings?.(block, indexPath, blockPath) || {}) as T

    const transformedBlock = {
        ...block,
        name: blockTemplate.name,
        ...transformedBindings,
        ...getBlockValue(block, blockTemplate),
        options: getBlockOptions((block as ComponentsWithOptions).configuration),
        internalData: omit(['component'], blockTemplate),
        layout: block.layout,
        display: block.display,
        indexPath,
        blockPath,
    }

    const cachedBlock = blocksCache.get(block.id)

    if (cachedBlock && !skipCache && equals(cachedBlock, transformedBlock)) {
        return cachedBlock // same instance
    }

    blocksCache.set(block.id, transformedBlock)
    return transformedBlock
}

export type getCustomPageBlockComponent = (block: ExtendedBlock) => ComponentInstance<any>

export const getCustomPageBlockComponent: getCustomPageBlockComponent = (block) => {
    const translatedBlock = BLOCK_MAP[block.type as keyof typeof BLOCK_MAP]

    return translatedBlock.component()
}

export const customPageBlockMap = (blocks: ApiCustomPageComponents[]): ExtendedBlock[] => {
    const isCp2 = blocks[0]?.type === 'CONTAINER'
    return blocks.map((block, blockIndex) => transformCustomPageBlock(block, [blockIndex], isCp2 ? [blockIndex] : undefined))
}

export function transformDateBlockBindings(_format: string, type: InputDate['type'] | InputDateTime['type'], value?: string) {
    if (!value) {
        return ''
    }

    const defaultFormat = type === 'INPUT_DATE_TIME'
        ? 'YYYY-MM-DD HH:mm'
        : 'YYYY-MM-DD'

    const format = `${ _format || defaultFormat }`

    const dates = [
        dayjs(value),
        dayjs(value, format),
    ]

    const d = dates.find(d => d.isValid())

    if (!d) return ''

    return d.format(format)
}
